/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ArrowDownIcon, ArrowUpIcon, CloseIcon, SearchIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  Spinner,
  Table,
  TableContainer,
  TableProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  ThemingProps,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  ColumnDef,
  HeaderGroup,
  OnChangeFn,
  PaginationState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  TableOptions,
  RowSelectionState,
  ColumnFilter,
} from '@tanstack/react-table';
import React, { ChangeEventHandler, useEffect, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { ColorMap } from '@/themes/Attributes';

export type AdvancedTableProps<T extends object> = {
  columns: ColumnDef<T, unknown>[];
  pageSize?: number;
  data: T[];
  searchEnabled?: boolean;
  HeaderLeftComponents?: React.ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  errorMsg?: string;
  noDataMsg?: string;
  paginationState?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  manualPagination?: boolean;
  pageCount?: number;
  size?: ThemingProps<'Table'>['size'];
  selection?: {
    rowSelection: RowSelectionState;
    setRowSelection: OnChangeFn<RowSelectionState>;
  };
  autoResetPageIndex?: boolean;
  filters?: ColumnFilter[];
  initialGlobalFilter?: string;
  tableProps?: TableProps;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
};
export function AdvancedTable<T extends object = Record<string, unknown>>(props: AdvancedTableProps<T>) {
  'use no memo';
  const {
    columns,
    data,
    searchEnabled,
    HeaderLeftComponents,
    pageSize = 10,
    paginationState,
    manualPagination = false,
    onPaginationChange,
    pageCount,
    filters,
    initialGlobalFilter,
  } = props;

  const initialGlobalFilterValue = initialGlobalFilter ?? '';

  const commonProps: TableOptions<T> = {
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: !!props.selection,
    onRowSelectionChange: props.selection?.setRowSelection ?? undefined,
    debugTable: true,
    autoResetPageIndex: props.autoResetPageIndex ?? true,
  };

  // Pass different props based on whether manualPagination is enabled
  const paginationProps = manualPagination
    ? {
        initialState: { pagination: { pageSize }, globalFilter: initialGlobalFilterValue },
        state: {
          rowSelection: props.selection?.rowSelection ?? undefined,
          pagination: paginationState,
        },
        pageCount,
        manualPagination,
        onPaginationChange,
      }
    : {
        initialState: { pagination: { pageSize }, globalFilter: initialGlobalFilterValue },
        getPaginationRowModel: getPaginationRowModel(),
        state: {
          rowSelection: props.selection?.rowSelection ?? undefined,
        },
      };

  const table = useReactTable({
    ...commonProps,
    ...paginationProps,
  });

  useEffect(() => {
    if (filters && filters.length > 0) {
      table.setColumnFilters((old) => [...old, ...filters]);
    }
  }, [table, filters]);

  const [searchValue, setSearchValue] = useState<string>(initialGlobalFilterValue);

  const handleSearchValueChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    const nextValue = e.target.value ?? '';
    setSearchValue(nextValue);
    table.setGlobalFilter(nextValue);
  };

  useEffect(() => {
    // Keep in sync if the prop changes after mount (e.g. URL param changes),
    // while avoiding redundant updates that can cause extra renders.
    const nextFilterValue = initialGlobalFilter ?? '';
    const currentFilterValue = (table.getState().globalFilter as string | undefined) ?? '';
    if (nextFilterValue === currentFilterValue) return;

    setSearchValue(nextFilterValue);
    table.setGlobalFilter(nextFilterValue);
  }, [initialGlobalFilter, table]);

  const handleSearchReset = () => {
    setSearchValue('');
    table.setGlobalFilter('');
  };

  if (props.isLoading && data.length === 0) {
    return (
      <Flex justifyContent="center">
        <Spinner />
      </Flex>
    );
  }
  if (props.isError) {
    return (
      <Flex color="red" justifyContent="center">
        {props.errorMsg ?? 'Something went wrong'}
      </Flex>
    );
  }

  return (
    <VStack width="100%" alignItems="stretch">
      {data.length > 0 && (
        <HStack mb={'20px'} pointerEvents={props.isLoading ? 'none' : 'auto'} opacity={props.isLoading ? 0.6 : 1}>
          {HeaderLeftComponents}
          {searchEnabled && (
            <GlobalTableSearch
              value={searchValue ?? ''}
              onChange={handleSearchValueChange}
              onReset={handleSearchReset}
            />
          )}
        </HStack>
      )}

      <Box position="relative">
        {props.isLoading && data.length > 0 && (
          <Flex position="absolute" inset={0} zIndex={1} align="center" justify="center" bg="whiteAlpha.600">
            <Spinner size="md" thickness="2px" color={ColorMap.stockGray} />
          </Flex>
        )}
        <TableContainer opacity={props.isLoading ? 0.4 : 1} pointerEvents={props.isLoading ? 'none' : 'auto'}>
          <Table variant="simple" size={props.size ?? { base: 'sm', md: 'md' }} overflowX="auto" {...props.tableProps}>
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeader headerGroup={headerGroup} key={headerGroup.id} />
              ))}
            </Thead>
            <Tbody fontWeight="normal">
              {data.length > 0 ? (
                table.getRowModel().rows.map((row) => {
                  return (
                    <Tr key={row.id}>
                      {row.getVisibleCells().map((cell) => {
                        return <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>;
                      })}
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={props.columns.length} color={ColorMap.stockGray} textAlign="center">
                    {props.noDataMsg ?? 'No data available'}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
      {data.length > 0 && (
        <PaginationBar
          paginationState={table.getState().pagination}
          canNextPage={table.getCanNextPage()}
          canPreviousPage={table.getCanPreviousPage()}
          count={table.getPageCount()}
          nextPage={table.nextPage}
          prevPage={table.previousPage}
          setPageIndex={table.setPageIndex}
          onPageSizeChange={props.onPageSizeChange}
          pageSizeOptions={props.pageSizeOptions}
          isDisabled={props.isLoading}
        />
      )}
    </VStack>
  );
}

function TableHeader<T>(props: { headerGroup: HeaderGroup<T> }) {
  const { headerGroup } = props;
  return (
    <Tr key={headerGroup.id}>
      {headerGroup.headers.map((header) => {
        const headerElement = flexRender(header.column.columnDef.header, header.getContext());
        const sort = header.column.getIsSorted();
        return (
          <Th key={header.id} colSpan={header.colSpan}>
            <Flex align={'center'} gap={'10px'}>
              <Box as="span">{headerElement}</Box>
              <Box as="span">
                {sort === 'desc' && <ArrowDownIcon boxSize={3} ml={2} />}
                {sort === 'asc' && <ArrowUpIcon boxSize={3} ml={2} />}
              </Box>
            </Flex>
          </Th>
        );
      })}
    </Tr>
  );
}

type GlobalTableSearchProps = {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onReset: () => void;
};
function GlobalTableSearch(props: GlobalTableSearchProps) {
  const { value, onChange, onReset } = props;
  return (
    <InputGroup width="25%" minW="300px">
      <InputLeftElement pointerEvents="none" children={<SearchIcon color="gray.300" />} />
      <Input type="text" value={value} onChange={onChange} placeholder={`Search...`} />
      {value && (
        <InputRightElement
          cursor={'pointer'}
          children={<CloseIcon fontSize={14} _hover={{ color: 'gray.600' }} color="gray.300" />}
          onClick={onReset}
        />
      )}
    </InputGroup>
  );
}

type PaginationBarProps = {
  paginationState: PaginationState;
  count: number;
  canPreviousPage: boolean;
  canNextPage: boolean;
  setPageIndex: (index: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isDisabled?: boolean;
};
function PaginationBar(props: PaginationBarProps) {
  const {
    paginationState,
    count,
    canPreviousPage,
    canNextPage,
    nextPage,
    prevPage,
    setPageIndex,
    onPageSizeChange,
    pageSizeOptions,
    isDisabled,
  } = props;
  const { pageIndex, pageSize } = paginationState;
  const gotoFirst = () => setPageIndex(0);
  const gotoLast = () => setPageIndex(count - 1);
  return (
    <Flex
      align={'center'}
      justify={'end'}
      gap={'10px'}
      pointerEvents={isDisabled ? 'none' : 'auto'}
      opacity={isDisabled ? 0.6 : 1}
    >
      {onPageSizeChange && pageSizeOptions && (
        <HStack spacing={1}>
          <Text fontSize="sm" whiteSpace="nowrap">
            Rows:
          </Text>
          <Select size="xs" width="70px" value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Select>
        </HStack>
      )}
      <Text mr={2} fontSize="sm">
        Page{'  '}
        <strong>
          {count ? pageIndex + 1 : count} of {count}
        </strong>
      </Text>
      <Button size={'xs'} onClick={gotoFirst} isDisabled={!canPreviousPage}>
        <Icon boxSize={4} as={FiChevronsLeft} />
      </Button>
      <Button size={'xs'} onClick={prevPage} isDisabled={!canPreviousPage}>
        <Icon boxSize={4} as={FiChevronLeft} />
      </Button>
      <Button size={'xs'} onClick={nextPage} isDisabled={!canNextPage}>
        <Icon boxSize={4} as={FiChevronRight} />
      </Button>
      <Button size={'xs'} onClick={gotoLast} isDisabled={!canNextPage}>
        <Icon boxSize={4} as={FiChevronsRight} />
      </Button>
    </Flex>
  );
}
